import type { McapTypes } from "@mcap/core";

import { MessageChunk } from "./MessageChunk";

export class MessageChunks<T> {
  #chunks: MessageChunk<T>[];
  #minTime: bigint | undefined = undefined;
  #maxTime: bigint | undefined = undefined;

  constructor(chunkIndices: readonly McapTypes.ChunkIndex[]) {
    // Downstream logic assumes chunks are in ascending order.
    const sortedChunkIndices = chunkIndices.slice().sort((a, b) => {
      if (a.messageStartTime === b.messageStartTime) {
        // break ties based on where chunk appears in the file
        return a.chunkStartOffset < b.chunkStartOffset ? -1 : 1;
      }
      return a.messageStartTime < b.messageStartTime ? -1 : 1;
    });

    const chunks = sortedChunkIndices.map(
      (chunkIndexRecord, idx) => new MessageChunk<T>(chunkIndexRecord, idx),
    );

    this.#chunks = chunks;
  }

  /** Latest end time across all chunks, or `undefined` when there are no chunks. */
  public get maxTime(): bigint | undefined {
    if (this.#maxTime === undefined && this.#chunks.length > 0) {
      this.#maxTime = this.#chunks[0].end;
      for (const chunk of this.#chunks) {
        if (chunk.end > this.#maxTime) {
          this.#maxTime = chunk.end;
        }
      }
    }
    return this.#maxTime;
  }

  /** Earliest start time across all chunks, or `undefined` when there are no chunks. */
  public get minTime(): bigint | undefined {
    if (this.#minTime === undefined && this.#chunks.length > 0) {
      this.#minTime = this.#chunks[0].start;
      for (const chunk of this.#chunks) {
        if (chunk.start < this.#minTime) {
          this.#minTime = chunk.start;
        }
      }
    }
    return this.#minTime;
  }

  public dispose(): void {
    for (const chunk of this.#chunks) {
      chunk.unload();
    }
  }

  /**
   * Returns the chunk that contains the given timepoint,
   * or the chunk that ends closest to the given timepoint.
   */
  public getChunkAsOfTime(searchTimeNs: bigint): MessageChunk<T> | undefined {
    if (this.#chunks.length === 0) {
      return;
    }

    let low = 0;
    let high = this.#chunks.length - 1;
    let result: MessageChunk<T> | undefined = undefined;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const chunk = this.#chunks[mid];
      if (chunk.containsTime(searchTimeNs)) {
        // A chunk contains a message published around the given timepoint.
        // Short-circuit the search.
        return chunk;
      } else if (chunk.end < searchTimeNs) {
        // The search timepoint is after the end of this chunk
        // Set chunk as our best candidate.
        result = chunk;
        low = mid + 1;
      } else {
        // The search timepoint must be before the start of this chunk
        high = mid - 1;
      }
    }
    return result;
  }

  public getChunksContainingTimestamps(
    timestamps: readonly bigint[],
  ): MessageChunk<T>[] {
    const chunks = new Set<MessageChunk<T>>();

    // store the last found chunks
    // and check if the next timestamp falls within any of them before doing another search
    // this optimizes for the expected case in which timestamps are ordered
    // and the common case in which messages logged close in time are located in the same chunk
    let lastChunks: MessageChunk<T>[] = [];
    for (const timestamp of timestamps) {
      if (lastChunks.some((chunk) => chunk.containsTime(timestamp))) {
        continue;
      }
      lastChunks = this.getChunksAsOfTime(timestamp);
      for (const chunk of lastChunks) {
        chunks.add(chunk);
      }
    }
    return Array.from(chunks).sort((a, b) => a.index - b.index);
  }

  public getChunksInRange({
    start,
    end,
  }: {
    start?: bigint | ((chunk: MessageChunk) => boolean);
    end?: bigint | ((chunk: MessageChunk) => boolean);
  }): MessageChunk<T>[] {
    return this.#chunks.filter((chunk) => {
      const startCondition = start ?? chunk.start;
      const endCondition = end ?? chunk.end;

      const startConditionTest =
        typeof startCondition === "function"
          ? startCondition(chunk)
          : startCondition <= chunk.end;
      const endConditionTest =
        typeof endCondition === "function"
          ? endCondition(chunk)
          : endCondition >= chunk.start;
      return startConditionTest && endConditionTest;
    });
  }

  public getLoadingChunks(): MessageChunk<T>[] {
    return this.#chunks.filter((chunk) => chunk.isLoading());
  }

  /**
   * Returns all chunks that contain the given timepoint.
   * Uses binary search to find an initial match, then scans adjacent chunks
   * to collect any overlapping chunks that also contain the timestamp.
   * If no chunk contains the timestamp, returns the chunk that ends closest
   * to the given timepoint (same fallback behavior as getChunkAsOfTime).
   */
  private getChunksAsOfTime(searchTimeNs: bigint): MessageChunk<T>[] {
    const initialChunk = this.getChunkAsOfTime(searchTimeNs);
    if (initialChunk === undefined) {
      return [];
    }

    // If the chunk doesn't contain the timestamp, it's a fallback (closest ending chunk)
    if (!initialChunk.containsTime(searchTimeNs)) {
      return [initialChunk];
    }

    const result: MessageChunk<T>[] = [initialChunk];
    const foundIndex = initialChunk.index;

    // Scan left for overlapping chunks
    for (let i = foundIndex - 1; i >= 0; i--) {
      if (this.#chunks[i].containsTime(searchTimeNs)) {
        result.push(this.#chunks[i]);
      } else {
        break;
      }
    }

    // Scan right for overlapping chunks
    for (let i = foundIndex + 1; i < this.#chunks.length; i++) {
      if (this.#chunks[i].containsTime(searchTimeNs)) {
        result.push(this.#chunks[i]);
      } else {
        break;
      }
    }

    return result;
  }
}
